import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DailyIframe from '@daily-co/daily-js';
import { AlertCircle, GraduationCap, MessageSquare, PhoneOff, Send, User, X } from 'lucide-react';
import MentorzaLogo from '../brand/MentorzaLogo';
import { completeMeetingRoom, sendMeetingEvent } from '../../services/meetingRouterService';
import {
  initSocket,
  joinRoom,
  leaveRoom,
  sendChatMessage as sendSocketChatMessage,
  onChatMessage
} from '../../services/socketService';

const jitsiScriptPromises = new Map();
const EMPTY_PROVIDER_CONFIG = {};

function buildProviderUrl(provider, config = {}) {
  if (provider === 'daily') {
    if (!config.url) return '';
    try {
      const url = new URL(config.url);
      if (config.token) url.searchParams.set('t', config.token);
      return url.toString();
    } catch {
      return '';
    }
  }

  if (provider === 'jitsi') {
    if (!config.room_name) return '';
    try {
      const url = new URL(`https://${config.domain || '8x8.vc'}/${config.room_name}`);
      if (config.jwt) url.searchParams.set('jwt', config.jwt);
      return url.toString();
    } catch {
      return '';
    }
  }

  return '';
}

function displayNameForUser(user = {}) {
  return user.display_name || user.name || (['mentor', 'doctor'].includes(user.role) ? 'Mentor' : 'Student');
}

function serializeProviderEvent(event) {
  if (!event || typeof event !== 'object') return {};
  return {
    action: event.action || event.type || null,
    displayName: event.displayName || event.participant?.displayName || null,
    errorMsg: event.errorMsg || event.error?.msg || event.message || null,
    id: event.id || event.participant?.id || null,
    name: event.name || null,
    roomName: event.roomName || null
  };
}

function providerErrorMessage(event, fallback) {
  return (
    event?.errorMsg ||
    event?.error?.msg ||
    event?.message ||
    event?.details?.message ||
    fallback
  );
}

function normalizeJitsiScriptUrl(config = {}) {
  const domain = config.domain || '8x8.vc';
  const configuredUrl = config.external_api_url || `https://${domain}/external_api.js`;

  try {
    const url = new URL(configuredUrl);
    if (url.hostname === domain && url.pathname.endsWith('/external_api.js')) {
      url.pathname = '/external_api.js';
    }
    return url.toString();
  } catch {
    return `https://${domain}/external_api.js`;
  }
}

function loadJitsiExternalApi(scriptUrl) {
  if (window.JitsiMeetExternalAPI) return Promise.resolve();
  if (jitsiScriptPromises.has(scriptUrl)) return jitsiScriptPromises.get(scriptUrl);

  const promise = new Promise((resolve, reject) => {
    const existingScript = Array.from(document.scripts).find((script) => script.src === scriptUrl);
    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Jitsi API script failed to load'));
    document.head.appendChild(script);
  });

  jitsiScriptPromises.set(scriptUrl, promise);
  return promise;
}

function DailyControlledFrame({ config = {}, ended, onError, onJoined, onLeft, user }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || ended) return undefined;
    if (!config.url) {
      onError('Daily room URL is missing');
      return undefined;
    }

    let disposed = false;
    containerRef.current.innerHTML = '';

    const callFrame = DailyIframe.createFrame(containerRef.current, {
      allowMultipleCallInstances: true,
      iframeStyle: {
        border: '0',
        display: 'block',
        height: '100%',
        width: '100%'
      },
      showFullscreenButton: true,
      showLeaveButton: false
    });

    const handleJoined = (event) => {
      if (!disposed) onJoined(serializeProviderEvent(event));
    };
    const handleLeft = (event) => {
      if (!disposed) onLeft(serializeProviderEvent(event));
    };
    const handleError = (event) => {
      if (!disposed) onError(providerErrorMessage(event, 'Daily call error'), serializeProviderEvent(event));
    };

    callFrame.on('joined-meeting', handleJoined);
    callFrame.on('left-meeting', handleLeft);
    callFrame.on('camera-error', handleError);
    callFrame.on('error', handleError);

    callFrame.join({
      token: config.token,
      url: config.url,
      userName: displayNameForUser(user)
    }).catch(handleError);

    return () => {
      disposed = true;
      callFrame.off('joined-meeting', handleJoined);
      callFrame.off('left-meeting', handleLeft);
      callFrame.off('camera-error', handleError);
      callFrame.off('error', handleError);
      Promise.resolve(callFrame.leave())
        .catch(() => {})
        .finally(() => {
          try {
            if (!callFrame.isDestroyed?.()) callFrame.destroy();
          } catch (_) {
            // Daily cleanup should never block the Mentorzaa session from closing.
          }
        });
    };
  }, [config.token, config.url, ended, onError, onJoined, onLeft, user]);

  return <div ref={containerRef} className="provider-frame controlled-provider-frame" />;
}

function JitsiControlledFrame({ config = {}, ended, onError, onJoined, onLeft, user }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || ended) return undefined;

    const domain = config.domain || '8x8.vc';
    const roomName = config.room_name || (config.app_id && config.channel ? `${config.app_id}/${config.channel}` : config.channel);
    if (!roomName) {
      onError('Jitsi room name is missing');
      return undefined;
    }

    let disposed = false;
    let api = null;
    const scriptUrl = normalizeJitsiScriptUrl(config);

    const handleJoined = (event) => {
      if (!disposed) onJoined(serializeProviderEvent(event));
    };
    const handleLeft = (event) => {
      if (!disposed) onLeft(serializeProviderEvent(event));
    };
    const handleError = (event) => {
      if (!disposed) onError(providerErrorMessage(event, 'Jitsi call error'), serializeProviderEvent(event));
    };

    containerRef.current.innerHTML = '';
    loadJitsiExternalApi(scriptUrl)
      .then(() => {
        if (disposed) return;
        api = new window.JitsiMeetExternalAPI(domain, {
          configOverwrite: {
            prejoinPageEnabled: false,
            startWithAudioMuted: false,
            startWithVideoMuted: false
          },
          height: '100%',
          interfaceConfigOverwrite: {
            DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
            MOBILE_APP_PROMO: false,
            SHOW_JITSI_WATERMARK: false,
            VIDEO_LAYOUT_FIT: 'both'
          },
          jwt: config.jwt,
          parentNode: containerRef.current,
          roomName,
          userInfo: {
            displayName: displayNameForUser(user)
          },
          width: '100%'
        });

        api.addListener('videoConferenceJoined', handleJoined);
        api.addListener('videoConferenceLeft', handleLeft);
        api.addListener('readyToClose', handleLeft);
        api.addListener('cameraError', handleError);
        api.addListener('micError', handleError);
        api.addListener('errorOccurred', handleError);
      })
      .catch((error) => {
        if (!disposed) onError(error.message || 'Jitsi API script failed to load');
      });

    return () => {
      disposed = true;
      if (!api) return;
      api.removeListener('videoConferenceJoined', handleJoined);
      api.removeListener('videoConferenceLeft', handleLeft);
      api.removeListener('readyToClose', handleLeft);
      api.removeListener('cameraError', handleError);
      api.removeListener('micError', handleError);
      api.removeListener('errorOccurred', handleError);
      try {
        api.executeCommand('hangup');
      } catch (_) {
        // The provider may already be closed.
      }
      api.dispose();
    };
  }, [config, ended, onError, onJoined, onLeft, user]);

  return <div ref={containerRef} className="provider-frame controlled-provider-frame" />;
}

function ProviderRenderer({ config, ended, fallbackUrl, onError, onJoined, onLeft, provider, user }) {
  if (provider === 'daily') {
    return (
      <DailyControlledFrame
        config={config}
        ended={ended}
        onError={onError}
        onJoined={onJoined}
        onLeft={onLeft}
        user={user}
      />
    );
  }

  if (provider === 'jitsi') {
    return (
      <JitsiControlledFrame
        config={config}
        ended={ended}
        onError={onError}
        onJoined={onJoined}
        onLeft={onLeft}
        user={user}
      />
    );
  }

  if (fallbackUrl) {
    return (
      <iframe
        title={`${provider} meeting`}
        allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-write"
        className="provider-frame"
        src={fallbackUrl}
      />
    );
  }

  return (
    <div className="waiting-room">
      <AlertCircle size={48} />
      <p>Preparing backup room...</p>
    </div>
  );
}

function statusLabel(status) {
  if (status === 'connected') return 'Connected';
  if (status === 'error') return 'Needs attention';
  if (status === 'left') return 'Left';
  return 'Connecting';
}

export default function ProviderFrameMeeting({
  bootstrap,
  embedded = false,
  joinToken,
  mentorAppEmbed = false,
  onClose,
  onRetry,
  prepareError = '',
  providerTokenData,
  roomClosed = false,
  roomUuid,
  studentAppEmbed = false
}) {
  const [showChat, setShowChat] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [ending, setEnding] = useState(false);
  const [ended, setEnded] = useState(false);
  const [providerStatus, setProviderStatus] = useState('connecting');
  const [providerError, setProviderError] = useState('');
  const [providerAttemptKey, setProviderAttemptKey] = useState(0);
  const joinEventSentRef = useRef(false);
  const leftEventSentRef = useRef(false);
  const closeStartedRef = useRef(false);
  // Socket channel used for real-time chat in non-Agora provider meetings.
  const socketChannelRef = useRef(null);

  const user = bootstrap?.user || {};
  const isMentor = Boolean(user.is_mentor || ['mentor', 'doctor'].includes(user.role));
  const student = bootstrap?.student || bootstrap?.patient || { name: 'Student' };
  const mentor = bootstrap?.mentor || bootstrap?.doctor || { name: 'Mentor' };
  const partnerName = isMentor ? (student.name || 'Student') : (mentor.name || 'Mentor');
  const provider = providerTokenData?.provider || bootstrap?.provider?.key;
  const providerConfig = providerTokenData?.config || EMPTY_PROVIDER_CONFIG;
  const hasProviderConfig = Boolean(providerTokenData?.config);
  const providerUrl = useMemo(
    () => buildProviderUrl(provider, providerConfig),
    [provider, providerConfig]
  );
  const canReconnect = true;

  // Real-time chat for non-Agora provider meetings via Socket.io
  useEffect(() => {
    if (!roomUuid) return;
    const channel = `provider_${roomUuid}`;
    socketChannelRef.current = channel;
    const userId = bootstrap?.user?.user_id || bootstrap?.user?.role || 'participant';
    const displayName = bootstrap?.user?.display_name || (['mentor', 'doctor'].includes(bootstrap?.user?.role) ? 'Mentor' : 'Student');

    initSocket(null, userId, displayName);
    joinRoom(channel, userId, displayName);

    const unsubscribe = onChatMessage((message) => {
      setChatMessages((prev) => [
        ...prev,
        {
          id: message.timestamp || Date.now(),
          senderName: message.senderName || 'Participant',
          text: message.text,
          timestamp: message.timestamp || new Date().toISOString(),
          isRemote: true
        }
      ]);
    });

    return () => {
      unsubscribe();
      leaveRoom(channel, userId);
    };
  }, [bootstrap?.user, roomUuid]);

  useEffect(() => {
    joinEventSentRef.current = false;
    leftEventSentRef.current = false;
    closeStartedRef.current = false;
    setEnded(false);
    setEnding(false);
  }, [joinToken, provider, roomUuid]);

  useEffect(() => () => {
    if (!joinEventSentRef.current || leftEventSentRef.current || !roomUuid || !joinToken) return;
    leftEventSentRef.current = true;
    sendMeetingEvent(roomUuid, joinToken, 'left', {
      provider,
      source: 'hosted_meeting_unmount'
    }).catch(() => {});
  }, [joinToken, provider, roomUuid]);

  useEffect(() => {
    if (ended) return;
    setProviderError(prepareError || '');
    setProviderStatus(prepareError ? 'error' : hasProviderConfig ? 'connecting' : 'preparing');
  }, [ended, hasProviderConfig, prepareError]);

  const handleSendMessage = async () => {
    const text = chatInput.trim();
    if (!text) return;
    const message = {
      id: Date.now(),
      senderName: user.display_name || 'You',
      text,
      timestamp: new Date().toISOString(),
      isRemote: false
    };
    setChatMessages((prev) => [...prev, message]);
    setChatInput('');

    // Broadcast to other participant via socket
    if (socketChannelRef.current) {
      sendSocketChatMessage({
        channelName: socketChannelRef.current,
        message: text,
        senderId: user.user_id || user.role,
        senderName: user.display_name || (user.is_mentor || user.is_doctor ? 'Mentor' : 'Student')
      });
    }

    // Also journal to meeting event log (best-effort)
    sendMeetingEvent(roomUuid, joinToken, 'chat', message).catch((error) => {
      console.error('Failed to save chat event:', error);
    });
  };

  const syncProviderLeft = useCallback(async (payload = {}) => {
    if (leftEventSentRef.current || !roomUuid || !joinToken) return;
    leftEventSentRef.current = true;
    await sendMeetingEvent(roomUuid, joinToken, 'left', {
      provider,
      ...payload
    }).catch((error) => {
      console.error('Failed to sync provider leave:', error);
    });
  }, [joinToken, provider, roomUuid]);

  const closeProviderMeeting = useCallback(async (payload = {}, { completeVisit = true } = {}) => {
    if (closeStartedRef.current) return;
    closeStartedRef.current = true;
    setEnding(true);

    try {
      await syncProviderLeft(payload);
      if (completeVisit && (user.is_mentor || user.is_doctor) && !embedded) {
        await completeMeetingRoom(roomUuid, joinToken, {
          notes: 'Mentorzaa session completed'
        });
      }
    } catch (error) {
      console.error('Failed to close meeting cleanly:', error);
    } finally {
      setEnding(false);
      setEnded(true);
      setProviderStatus('left');
    }

    if (onClose) onClose(payload);
  }, [embedded, joinToken, onClose, roomUuid, syncProviderLeft, user.is_doctor, user.is_mentor]);

  useEffect(() => {
    if (roomClosed && !ended) {
      closeProviderMeeting({ room_closed: true }, { completeVisit: false });
    }
  }, [closeProviderMeeting, ended, roomClosed]);

  const handleProviderJoined = useCallback(async (event = {}) => {
    setProviderStatus('connected');
    setProviderError('');
    if (joinEventSentRef.current || !roomUuid || !joinToken) return;
    joinEventSentRef.current = true;
    await sendMeetingEvent(roomUuid, joinToken, 'joined', {
      provider,
      provider_event: event
    }).catch((error) => {
      console.error('Failed to sync provider join:', error);
    });
  }, [joinToken, provider, roomUuid]);

  const handleProviderLeft = useCallback((event = {}) => {
    closeProviderMeeting({
      provider_event: event,
      provider_initiated: true
    });
  }, [closeProviderMeeting]);

  const handleProviderError = useCallback((message, event = {}) => {
    const text = message || 'Video provider error';
    setProviderStatus('error');
    setProviderError(text);
    if (roomUuid && joinToken) {
      sendMeetingEvent(roomUuid, joinToken, 'provider_error', {
        message: text,
        provider,
        provider_event: event
      }).catch(() => {});
    }
  }, [joinToken, provider, roomUuid]);

  const handleReconnect = useCallback(async () => {
    setEnding(true);
    setProviderError('');
    setProviderStatus('preparing');

    try {
      if (onRetry) {
        const refreshed = await onRetry();
        if (!refreshed) return;
      }

      joinEventSentRef.current = false;
      leftEventSentRef.current = false;
      closeStartedRef.current = false;
      setEnded(false);
      setProviderStatus('connecting');
      setProviderAttemptKey((value) => value + 1);
    } finally {
      setEnding(false);
    }
  }, [onRetry]);

  const handleEnd = () => {
    closeProviderMeeting({ ended_by: 'mentorzaa_controls' });
  };

  return (
    <div className={`app-container ${embedded ? 'embedded-call' : ''} ${studentAppEmbed ? 'patient-app-embed student-app-embed' : mentorAppEmbed ? 'doctor-app-embed mentor-app-embed' : embedded ? 'workspace-embed' : ''}`}>
      <div className="call-header">
        <div className="call-info">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <MentorzaLogo variant="horizontal" height={26} />
            {user.display_name && <span className="user-name">{user.display_name}</span>}
          </div>
          <div className="call-meta">
            <span className="channel-badge">Provider: {provider || 'preparing'}</span>
            <span className={`provider-status ${providerStatus}`}>{statusLabel(providerStatus)}</span>
            <span className={`role-badge ${isMentor ? 'mentor' : 'student'}`}>
              {isMentor ? <GraduationCap size={12} /> : <User size={12} />}
              {isMentor ? 'Mentor' : 'Student'}
            </span>
            <span className="duration-badge">Session with: {partnerName}</span>
          </div>
        </div>
      </div>

      <div className="call-content">
        <div className="video-area">
          {studentAppEmbed && !isMentor && (
            <div className="patient-call-strip">
              <span className="live-dot" />
              <span>Mentor video is primary. Your camera and microphone controls stay within reach.</span>
            </div>
          )}
          {mentorAppEmbed && isMentor && (
            <div className="patient-call-strip doctor-call-strip">
              <span className="live-dot" />
              <span>Student video is primary. Close the meeting to return to your Mentorzaa sessions.</span>
            </div>
          )}

          <div className="provider-frame-wrap">
            {ended ? (
              <div className="waiting-room ended-room">
                <PhoneOff size={48} />
                <p>Call ended</p>
                <span>
                  {roomClosed
                    ? 'This secure session is no longer open.'
                    : embedded
                    ? 'Return to Mentorzaa to continue your work.'
                    : 'You can now return to your Mentorzaa sessions.'}
                </span>
                {prepareError && <span className="provider-rejoin-error">{prepareError}</span>}
                {!roomClosed && canReconnect && (
                  <button
                    className="provider-reconnect-button"
                    disabled={ending}
                    onClick={handleReconnect}
                    type="button"
                  >
                    {ending ? 'Reconnecting...' : 'Rejoin call'}
                  </button>
                )}
              </div>
            ) : (
              <>
                {hasProviderConfig ? (
                  <ProviderRenderer
                    key={`${provider || 'provider'}-${providerAttemptKey}`}
                    config={providerConfig}
                    ended={ended}
                    fallbackUrl={providerUrl}
                    onError={handleProviderError}
                    onJoined={handleProviderJoined}
                    onLeft={handleProviderLeft}
                    provider={provider}
                    user={user}
                  />
                ) : (
                  <div className="waiting-room provider-preparing-room">
                    <p>{prepareError ? 'Secure call setup needs attention.' : `Preparing secure ${provider || 'video'} room...`}</p>
                  </div>
                )}
                <div className={`provider-status-pill ${providerStatus}`}>
                  {statusLabel(providerStatus)}
                </div>
                {providerError && (
                  <div className="provider-error">
                    <AlertCircle size={16} />
                    <span>{providerError}</span>
                    <button disabled={ending} onClick={handleReconnect} type="button">
                      {ending ? 'Retrying...' : 'Reconnect'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="controls-bar">
            <div className="control-buttons">
              <button
                className={`control-btn chat-btn ${showChat ? 'active' : ''}`}
                onClick={() => setShowChat(!showChat)}
                aria-label={showChat ? 'Close chat' : 'Open chat'}
                title="Chat"
              >
                <MessageSquare />
                <span className="control-label">Chat</span>
              </button>

              <button className="control-btn hangup" onClick={handleEnd} disabled={ending || ended} title="End Call">
                <PhoneOff />
                <span className="hangup-text">Leave</span>
              </button>
            </div>
          </div>
        </div>

        {showChat && (
          <div className="chat-panel">
            <div className="chat-header">
              <h3>Chat</h3>
              <button className="close-btn" onClick={() => setShowChat(false)}>
                <X />
              </button>
            </div>
            <div className="chat-messages">
              {chatMessages.length === 0 ? (
                <p className="no-messages">No messages yet</p>
              ) : (
                chatMessages.map((message) => (
                  <div key={message.id} className="chat-message sent">
                    <div className="message-sender">{message.senderName}</div>
                    <div className="message-text">{message.text}</div>
                    <div className="message-time">
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="chat-input">
              <input
                type="text"
                placeholder="Type a message..."
                value={chatInput}
                onChange={(event) => setChatInput(event.target.value)}
                onKeyPress={(event) => event.key === 'Enter' && handleSendMessage()}
              />
              <button onClick={handleSendMessage}>
                <Send />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
